import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Reveal } from '../components/Reveal';
import { PhoneFrame } from '../components/PhoneFrame';
import { SnakExperience } from '../components/experience/SnakExperience';
import { PitchActions } from '../components/pitch/PitchActions';
import { FormatToggle } from '../components/pitch/FormatToggle';
import { ConceptOverlay } from '../components/pitch/ConceptOverlay';
import { SignalsOverlay } from '../components/pitch/SignalsOverlay';
import { StoryboardOverlay } from '../components/pitch/StoryboardOverlay';
import { PlayOverlay } from '../components/pitch/PlayOverlay';
import type { Format } from '../types/snak';
interface PitchProps {
  showProgress: boolean;
  autoplayInterlude: boolean;
}
type Layer = 'none' | 'concept' | 'signals' | 'storyboard' | 'play';
const PHONE_LABEL = 'Live mobile prototype of the Meet the Filtek’s Snak';
export function Pitch({
  showProgress,
  autoplayInterlude
}: PitchProps) {
  const [layer, setLayer] = useState<Layer>('none');
  const [format, setFormat] = useState<Format>('mobile');
  const close = () => setLayer('none');
  return <div className="relative flex min-h-screen w-full flex-col bg-[#F7F5F0] font-body antialiased" data-id="element-441" data-mp="1i70d3k14in75">
      <div className="flex items-center justify-between gap-4 px-8 pt-6 lg:px-14" data-id="element-442" data-mp="1478vl9beeooe">
        <span className="font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.35)]" data-id="element-443" data-mp="195k1xa2hih1v">
          Meet the Filtek’s · Snakker concept
        </span>
        <FormatToggle value={format} onChange={setFormat} data-id="element-444" data-mp="21t6e36ps2u0c" />
      </div>

      <main className="flex flex-1 items-center" data-id="element-445" data-mp="21v959u2o2t6n">
        <div className="mx-auto grid w-full max-w-[1440px] items-center gap-14 px-8 py-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-20 lg:px-14 lg:py-8" data-id="element-446" data-mp="1n3igafgt2dhd">
          <div className="max-w-[560px]" data-id="element-447" data-mp="1s1a8zbp9y3mm">
            <Reveal data-id="element-448" data-mp="2fs652l0rjkzo">
              <h1 className="font-heading text-[58px] leading-[0.92] tracking-[-0.035em] text-background sm:text-[76px]" data-id="element-449" data-mp="2frmop8qrh4nr">
                Meet the Filtek’s
              </h1>
            </Reveal>
            <Reveal delay={0.1} data-id="element-450" data-mp="2ufe9n2zkgs2o">
              <p className="mt-7 max-w-[26ch] font-heading text-[21px] leading-[1.3] tracking-[-0.01em] text-[rgba(1,51,43,0.6)]" data-id="element-451" data-mp="2udjz8ilx8icu">
                A playful way to turn a product family into a family people can actually
                understand.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-12" data-id="element-452" data-mp="2gi7v5klvlfj9">
              <PitchActions onPlay={() => setLayer('play')} onStoryboard={() => setLayer('storyboard')} onConcept={() => setLayer('concept')} onSignals={() => setLayer('signals')} data-id="element-453" data-mp="2bhbox0e1xhao" />
            </Reveal>
          </div>

          <Reveal delay={0.16} y={26} className="flex justify-center lg:justify-end" data-id="element-454" data-mp="1wsxf25i7wskm">
            <div className="flex flex-col items-center gap-5" data-id="element-455" data-mp="1sr2ej3842j7w">
              <span className="font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.35)]" data-id="element-456" data-mp="1ssn7ioes1u3n">
                Live prototype
              </span>
              <PhoneFrame label={PHONE_LABEL} sizeClassName="h-[clamp(560px,calc(100vh-248px),840px)] w-auto aspect-[344/688]" data-id="element-457" data-mp="38dpnz8hlvbc">
                <SnakExperience showProgress={showProgress} autoplayInterlude={autoplayInterlude} data-id="element-458" data-mp="c9lmecvcuo3b" />
              </PhoneFrame>
              <span className="font-body text-[11px] tracking-[0.02em] text-[rgba(1,51,43,0.4)]" data-id="element-459" data-mp="3gobng6bjw5jo">
                Tap to play · Scroll inside the phone
              </span>
            </div>
          </Reveal>
        </div>
      </main>

      <footer className="px-8 pb-6 lg:px-14" data-id="element-460" data-mp="3porprcgk56my">
        <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[rgba(1,51,43,0.25)]" data-id="element-461" data-mp="3pmx1pp9zoyen">
          Concept &amp; creative direction © Snakker™ 2026 · Confidential &amp;
          proprietary
        </p>
      </footer>

      <AnimatePresence data-id="element-462" data-mp="31z5q6nw9wpef">
        {layer === 'concept' && <ConceptOverlay key="concept" format={format} onFormatChange={setFormat} onClose={close} data-id="element-463" data-mp="31xm58jzo9q6b" />}
        {layer === 'signals' && <SignalsOverlay key="signals" format={format} onFormatChange={setFormat} onClose={close} data-id="element-464" data-mp="13firum3fz1rw" />}
        {layer === 'storyboard' && <StoryboardOverlay key="storyboard" format={format} onFormatChange={setFormat} onClose={close} data-id="element-465" data-mp="1cggxf8tj3hcl" />}
        {layer === 'play' && <PlayOverlay key="play" format={format} showProgress={showProgress} autoplayInterlude={autoplayInterlude} onClose={close} data-id="element-466" data-mp="207w39bkikgsl" />}
      </AnimatePresence>
    </div>;
}